'use client';

import { useCallback, useEffect, useRef, type KeyboardEvent, type RefObject } from 'react';
import type { LoqateFindItem } from '../types';

type UseKeyboardNavigationProps = {
  open: boolean;
  suggestions: LoqateFindItem[];
  highlightedIndex: number;
  inputRef: RefObject<HTMLInputElement | null>;
  setHighlightedIndex: (index: number) => void;
  setOpen: (open: boolean) => void;
  onSelect: (suggestion: LoqateFindItem) => void;
};

function useKeyboardNavigation({ open, suggestions, highlightedIndex, inputRef, setHighlightedIndex, setOpen, onSelect }: UseKeyboardNavigationProps) {
  // Use a ref to track the previous highlighted index to prevent unnecessary updates
  const prevHighlightedIndexRef = useRef(highlightedIndex);

  // Handle keyboard events on the input (for opening the dropdown)
  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      // Open popover on ArrowDown if we have suggestions but it's not open yet
      if (e.key === 'ArrowDown' && !open && suggestions.length > 0) {
        e.preventDefault();
        setOpen(true);
        // Only update if different to prevent unnecessary renders
        if (prevHighlightedIndexRef.current !== 0) {
          setHighlightedIndex(0); // Select the first item
          prevHighlightedIndexRef.current = 0;
        }
      }
    },
    [open, suggestions.length, setOpen, setHighlightedIndex]
  );

  // Handle keyboard interaction for suggestion items
  const handleItemKeyDown = useCallback(
    (e: KeyboardEvent, suggestion: LoqateFindItem) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(suggestion);
      }
    },
    [onSelect]
  );

  // Set up global keyboard event listener for arrow keys
  useEffect(() => {
    const handleGlobalKeyDown = (e: globalThis.KeyboardEvent) => {
      // Only handle if dropdown is open and we have suggestions
      if (!open || suggestions.length === 0) return;

      // Only handle arrow keys and enter
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter' && e.key !== 'Escape') return;

      // Make sure the input has focus and exists
      if (!inputRef.current || document.activeElement !== inputRef.current) return;

      let nextIndex: number;
      let prevIndex: number;

      // Now handle the keys
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          nextIndex = highlightedIndex < suggestions.length - 1 ? highlightedIndex + 1 : 0;
          // Only update if different to prevent unnecessary renders
          if (prevHighlightedIndexRef.current !== nextIndex) {
            setHighlightedIndex(nextIndex);
            prevHighlightedIndexRef.current = nextIndex;
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          prevIndex = highlightedIndex > 0 ? highlightedIndex - 1 : suggestions.length - 1;
          // Only update if different to prevent unnecessary renders
          if (prevHighlightedIndexRef.current !== prevIndex) {
            setHighlightedIndex(prevIndex);
            prevHighlightedIndexRef.current = prevIndex;
          }
          break;

        case 'Enter':
          if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            e.preventDefault();
            const selectedSuggestion = suggestions[highlightedIndex];
            if (selectedSuggestion) {
              onSelect(selectedSuggestion);
            }
          }
          break;

        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;
      }
    };

    // Add the global event listener
    document.addEventListener('keydown', handleGlobalKeyDown);

    // Clean up
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [open, suggestions, highlightedIndex, inputRef, setHighlightedIndex, setOpen, onSelect]);

  return {
    handleInputKeyDown,
    handleItemKeyDown,
  };
}

export { useKeyboardNavigation };
